/**
 * Who the workshop is (#2465, reordered in #2467).
 *
 * Name, business ID, address and contacts under one heading. These were two
 * sections until #2467 — the first holding the name alone and filling half a
 * screen with it, the second holding everything else. A workshop's name and
 * its address are one thing, so the split was arbitrary.
 *
 * This is first on the tab because the server refuses to record a payment
 * without the business ID, the address and one of the contact fields
 * (receipt-seller.ts), which is exactly what the subtitle says.
 *
 * Bank details moved to the billing section: they are how money arrives, not
 * who the workshop is.
 */

import { Paper, Typography, TextField, Grid } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { useTranslation } from 'react-i18next';
import type { WorkshopSettings } from '@/types';
import { useBrandingStore } from '@/stores/brandingStore';
import {
  formatTimezonePreview,
  isValidIanaTimeZone,
  timezoneSelectOptions,
} from '@/features/settings/timezoneOptions';

interface Props {
  settings: WorkshopSettings;
  onChange: (settings: WorkshopSettings) => void;
}

export function WorkshopSectionIdentity({ settings, onChange }: Props) {
  const { t, i18n } = useTranslation('settings');
  const timezoneValid = isValidIanaTimeZone(settings.timezone);
  const timezonePreview = formatTimezonePreview(settings.timezone, new Date(), i18n.language);

  /**
   * The name on the invoice is not always this field (#2471).
   * `invoice-seller.ts` and `receipt-seller.ts` both resolve
   * `branding.companyName ?? tenant.name`, so a workshop can rename itself
   * here and watch the invoice ignore it.
   *
   * Read from the store rather than `useBranding()`, which substitutes a
   * product default when nothing is configured and would make this look set
   * when it is not. Shown only when a different name actually wins — and as
   * text rather than a link, since editing branding needs a permission the
   * person reading this may not have.
   */
  const brandingCompanyName = useBrandingStore(
    (state) => state.branding?.branding?.companyName,
  );
  const overriddenBy =
    typeof brandingCompanyName === 'string' &&
    brandingCompanyName.trim() !== '' &&
    brandingCompanyName.trim() !== settings.name.trim()
      ? brandingCompanyName.trim()
      : null;

  const handleChange = (field: keyof WorkshopSettings, value: string | number) => {
    onChange({ ...settings, [field]: value });
  };

  return (
    <>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {t('workshopInfo')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('invoiceReceiptDetailsHint')}
      </Typography>

      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 4 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('workshopName')}
              value={settings.name}
              onChange={(e) => handleChange('name', e.target.value)}
              helperText={
                overriddenBy !== null
                  ? t('workshopNameOverridden', { name: overriddenBy })
                  : undefined
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('businessId')}
              value={settings.businessId}
              onChange={(e) => handleChange('businessId', e.target.value)}
              helperText={t('businessIdHint')}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label={t('address')}
              value={settings.address}
              onChange={(e) => handleChange('address', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label={t('postalCode')}
              value={settings.postalCode}
              onChange={(e) => handleChange('postalCode', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label={t('city')}
              value={settings.city}
              onChange={(e) => handleChange('city', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label={t('country')}
              value={settings.country}
              onChange={(e) => handleChange('country', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('phone')}
              value={settings.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('email')}
              type="email"
              value={settings.email}
              onChange={(e) => handleChange('email', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              select
              fullWidth
              label={t('timezone.label')}
              value={settings.timezone}
              onChange={(e) => handleChange('timezone', e.target.value)}
              error={!timezoneValid}
              helperText={
                !timezoneValid
                  ? t('timezone.invalid')
                  : timezonePreview !== null
                    ? t('timezone.preview', {
                        time: timezonePreview.time,
                        offset: timezonePreview.offset,
                      })
                    : t('timezone.description')
              }
            >
              {timezoneSelectOptions(settings.timezone).map((zone) => (
                <MenuItem key={zone} value={zone}>
                  {zone}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
}
